import { Sun, Battery, BatteryCharging, Zap, Loader2 } from 'lucide-react'
import { useJourneyTrigger } from '../hooks/useJourneyTrigger'
import type { BPlateViewRowAction } from '../types'
import type { JourneyApi } from './PlateViewRenderer'

interface DeviceRow {
  id?: unknown
  vendor?: unknown
  deviceType?: unknown
  externalDeviceId?: unknown
  status?: unknown
  [key: string]: unknown
}

// Fixed slots for the mock fleet's three known device types (solar on the roof, battery + meter
// against the wall) — a home with more devices of the same type just stacks additional icons
// below the house rather than trying to invent a general-purpose floor-plan layout for a fleet
// that, today, is always exactly one of each.
const SLOTS: Record<string, { top: string; left: string }> = {
  solar: { top: '20%', left: '50%' },
  battery: { top: '62%', left: '30%' },
  meter: { top: '62%', left: '70%' },
}

const ICONS: Record<string, typeof Sun> = { solar: Sun, meter: Zap }
const COLORS: Record<string, string> = {
  solar: 'text-amber-500 bg-amber-50',
  battery: 'text-emerald-600 bg-emerald-50',
  meter: 'text-accent bg-accent-soft',
}

function isActive(status: string): boolean {
  return /Producing|Charging|Discharging|Consuming/.test(status)
}

function socFromStatus(status: string): number | null {
  const match = status.match(/\((\d+(?:\.\d+)?)%\)/)
  return match ? parseFloat(match[1]) : null
}

export function EnergyOSDeviceHouse({ devices, rowActions, runInputs, journeyApi }: {
  devices: DeviceRow[]
  rowActions: BPlateViewRowAction[]
  runInputs: Record<string, string>
  journeyApi: JourneyApi
}) {
  const chargeAction = rowActions.find(a => a.preparationId)
  const trigger = useJourneyTrigger(journeyApi)

  if (devices.length === 0) {
    return <p className="text-xs text-ink-dim text-center py-10">No devices paired yet</p>
  }

  const slotted = devices.filter(d => SLOTS[String(d.deviceType)])
  const overflow = devices.filter(d => !SLOTS[String(d.deviceType)])

  return (
    <div>
      <div className="relative w-full max-w-md mx-auto" style={{ aspectRatio: '4 / 3' }}>
        <svg viewBox="0 0 400 300" className="absolute inset-0 w-full h-full" aria-hidden="true">
          <polygon points="40,150 200,45 360,150" fill="#dde9ff" />
          <rect x="75" y="150" width="250" height="135" rx="6" fill="#f2f4f7" />
          <rect x="185" y="215" width="30" height="70" rx="2" fill="#d0d5dd" />
        </svg>

        {slotted.map((device, i) => {
          const deviceType = String(device.deviceType ?? '')
          const status = String(device.status ?? '')
          const slot = SLOTS[deviceType]
          const active = isActive(status)
          const soc = socFromStatus(status)
          const Icon = deviceType === 'battery' ? (status.includes('Charging') ? BatteryCharging : Battery) : (ICONS[deviceType] ?? Zap)
          const canCharge = deviceType === 'battery' && chargeAction &&
            (!chargeAction.showIf || String(device[chargeAction.showIf.field] ?? '') === chargeAction.showIf.equals)

          return (
            <div
              key={i}
              className="absolute flex flex-col items-center gap-1"
              style={{ top: slot.top, left: slot.left, transform: 'translate(-50%, -50%)' }}
            >
              <div className={`w-12 h-12 rounded-full shadow-dropdown flex items-center justify-center ${COLORS[deviceType] ?? 'text-ink-dim bg-paper-sunken'} ${active ? 'animate-pulse' : ''}`}>
                <Icon size={22} />
              </div>
              <p className="text-[10px] font-medium text-ink-dim bg-white/90 px-1.5 py-0.5 rounded whitespace-nowrap">
                {status || '—'}
              </p>
              {soc !== null && (
                <div className="w-10 h-1 bg-paper-border rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${soc}%` }} />
                </div>
              )}
              {canCharge && chargeAction && (
                <button
                  onClick={() => {
                    if (!chargeAction.preparationId) return
                    trigger.run(chargeAction.preparationId, { ...runInputs, [chargeAction.payloadKey]: String(device[chargeAction.idField] ?? '') })
                  }}
                  disabled={trigger.isBusy}
                  className="text-[10px] text-accent hover:underline disabled:opacity-50 flex items-center gap-0.5"
                >
                  {trigger.isBusy ? <Loader2 size={10} className="animate-spin" /> : null}
                  {trigger.status === 'completed' ? 'Done' : chargeAction.label}
                </button>
              )}
            </div>
          )
        })}
      </div>

      {overflow.length > 0 && (
        <div className="flex flex-wrap justify-center gap-4 mt-3">
          {overflow.map((device, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs text-ink-dim">
              <Zap size={14} className="text-ink-dim" />
              {String(device.vendor ?? '')} {String(device.deviceType ?? '')} — {String(device.status ?? '—')}
            </div>
          ))}
        </div>
      )}

      {trigger.error && <p className="text-xs text-red-500 text-center mt-2">{trigger.error}</p>}
    </div>
  )
}
